/** @jsx jsx */
import { customColors, customColorsRaw } from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import { useCallback, useEffect, useState } from 'react';

import {
  BarChart,
  BarChartProps,
} from 'src/components/charts/BarChart/BarChart';
import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import { Legend } from 'src/components/charts/LineChart/legend/Legend';
import { Loading } from 'src/components/common/loading/Loading';

import { transformMetricsToMinuteResolution } from './helpers/convertDataResolution';
import { normalizeMetrics } from './helpers/normalizeMetrics';
import { BarChartWrapperProps, GraphOSMetricsRecord } from './types/types';

// Custom colors for GraphOS Cloud Bar Chart
const barColors = [
  // Primary segment follows standard base styles
  {
    fill: `rgb(${customColorsRaw.flowchart.d} / 0.6)`,
    cursor: customColors.flowchart.d,
    marker: `rgb(${customColorsRaw.flowchart.c} / 0.6)`,
  },
  // Secondary segment is red to indicate 429/throttled requests
  {
    fill: `rgb(${customColorsRaw.brand.accent} / 0.6)`,
    cursor: `rgb(${customColorsRaw.brand.accent})`,
    marker: `rgb(${customColorsRaw.flowchart.c} / 0.6)`,
  },
];

/**
 * Wraps the BarChart component, handling data normalization, dynamic yAxis labels, and custom colors.
 * Specifically tailored for charts on the Cloud Settings page.
 *
 * @component
 * @param {Object} props - Props for BarChartWrapper.
 * @param {GraphOSMetrics} props.data - Metrics data for the chart.
 * @param {Object} props.metricPair - Primary and secondary metrics with legend info.
 * @param {string} props.metricPair.primary - Key for the primary metric.
 * @param {string} props.metricPair.secondary - Key for the secondary metric.
 * @param {Array<Object>} props.metricPair.legend - Legend array with `label` and `color`.
 * @param {boolean} props.loading - Loading state indicator.
 * @param {string} [props.yAxisLabel] - Optional yAxis label, auto-set if not provided.
 */
export const BarChartWrapper = ({
  data,
  metricPair,
  loading,
  yAxisLabel,
}: BarChartWrapperProps) => {
  // Set the yAxis label based on a condition. Currently used for the Response Size Chart.
  const [formatttedYAxisLabel, setFormattedYAxisLabel] = useState<string>(
    yAxisLabel || '',
  );

  const roundToTwoDecimals = (value: number | undefined): number => {
    return parseFloat((value ?? 0).toFixed(2));
  };

  const graphosCloudMetrics =
    data?.graph?.statsWindow?.graphosCloudMetrics ?? [];

  const minuteResolutionMetrics = graphosCloudMetrics.map(
    transformMetricsToMinuteResolution,
  );

  // Get the data and legend text for each bar chart.
  const { normalizedData, detectedUnit } = normalizeMetrics(
    minuteResolutionMetrics,
    [metricPair.primary, metricPair.secondary], // The two data segments being normalized (e.g., 'responseSize' vs. 'responseSizeThrottled')
  );

  // Set either `KB` or `MB` as units in the labels for
  // Response Size bar chart.
  const unitAdjustedLegend =
    metricPair.label === 'Response Size'
      ? metricPair.legend.map((item) => ({
          ...item,
          label: `${item.label} (${detectedUnit || 'KB'})`,
        }))
      : metricPair.legend;

  useEffect(() => {
    if (metricPair.label === 'Response Size' && detectedUnit) {
      setFormattedYAxisLabel(`response size (${detectedUnit})`);
    }
  }, [metricPair, detectedUnit]);

  // Initialize function needed to format data for Bar Chart.
  const getValue: BarChartProps<GraphOSMetricsRecord>['getValue'] = useCallback(
    (record: GraphOSMetricsRecord) => {
      // Return fallback data object if no data is returned
      if (!record || !record.metrics) {
        return {
          timestamp: record?.timestamp,
          value: 0,
          tooltipContent: 'No data available',
          stackCounts: [{ value: 0 }, { value: 0 }],
        };
      }

      const primaryMetricKey = metricPair.primary;
      const secondaryMetricKey = metricPair.secondary;

      const normalizedPrimaryMetric = record.metrics[primaryMetricKey] ?? 0;
      const normalizedSecondaryMetric = record.metrics[secondaryMetricKey] ?? 0;

      const primaryLegendEntry = metricPair.legend[0];
      const secondaryLegendEntry = metricPair.legend[1];

      // Choose the correct tooltip content function based on the chart type
      const tooltipContent =
        metricPair.label === 'Response Size'
          ? `${primaryLegendEntry?.label}: ${roundToTwoDecimals(
              normalizedPrimaryMetric,
            )} ${detectedUnit} | ${
              secondaryLegendEntry?.label
            }: ${roundToTwoDecimals(normalizedSecondaryMetric)} ${detectedUnit}`
          : `${primaryLegendEntry?.label}: ${roundToTwoDecimals(
              normalizedPrimaryMetric,
            )} | ${secondaryLegendEntry?.label}: ${roundToTwoDecimals(
              normalizedSecondaryMetric,
            )}`;

      return {
        timestamp: record.timestamp,
        value: normalizedPrimaryMetric + normalizedSecondaryMetric,
        tooltipContent,
        stackCounts: [
          { value: normalizedPrimaryMetric },
          { value: normalizedSecondaryMetric },
        ],
      };
    },
    [metricPair, detectedUnit],
  );

  return (
    <div className="mb-24">
      <h4 className="text-title mb-4">{metricPair.label}</h4>
      {loading ? (
        <Loading />
      ) : (
        <CursorProvider>
          <BarChart
            css={css`
              @media (min-width: 640px) {
                width: 500px;
              }
              @media (min-width: 768px) {
                width: 650px;
              }
              @media (min-width: 1024px) {
                width: 725px;
              }
            `}
            data={normalizedData}
            getValue={getValue}
            yAxisLabel={formatttedYAxisLabel}
            barColors={barColors}
            className="h-60"
          />
          <Legend
            keys={
              metricPair.label === 'Response Size'
                ? unitAdjustedLegend
                : metricPair.legend
            }
            className="mt-4"
          />
        </CursorProvider>
      )}
    </div>
  );
};
